import React, { useState } from "react";
import LoadingWidget from "../../../../context_providers/modal/loader";
import Icon from "@mdi/react";
import { mdiUndoVariant } from "@mdi/js";
import { WarehouseOutwardModel } from "../model/warehouse_outward_model";
import "../inward-styles.css";
import { InwardAttachmentUpload } from "../inward/InwardAttachmentUpload";
import { ReturnOutwardApi } from "../warehouse_module_repo";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";

type ReturnOutwardModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  selectedOutward?: WarehouseOutwardModel | null;
};

const ReturnOutwardModal: React.FC<ReturnOutwardModalProps> = ({
  open,
  onClose,
  onSuccess,
  selectedOutward,
}) => {
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { showToast } = useToast();

  if (!open || !selectedOutward) return null;

  const isReasonInvalid = reason.trim().length < 10;

  const handleClose = () => {
    if (loading) return;
    setReason("");
    setFiles([]);
    setError(null);
    onClose();
  };

  const handleReturnOutward = async () => {
    setError(null);

    if (!reason.trim()) {
      setError("Return reason is required.");
      return;
    }
    if (reason.trim().length < 10) {
      setError("Return reason must be at least 10 characters.");
      return;
    }

    const formData = new FormData();
    formData.append("reason", reason.trim());
    files.forEach((file) => formData.append("attachments", file));

    try {
      setLoading(true);
      const res = await ReturnOutwardApi(formData, selectedOutward.id!);

      if (!res.success) {
        showToast({
          type: ToastType.error,
          text: res.error || "Failed to mark outward as returned",
        });
        return;
      }

      showToast({
        type: ToastType.success,
        text: `${selectedOutward.type === "SO" ? "Order" : "Stock transfer"} marked as returned successfully`,
      });

      setReason("");
      setFiles([]);
      onSuccess?.();
      onClose();
    } catch (err: any) {
      showToast({
        type: ToastType.error,
        text: err?.message || "Something went wrong while marking as returned.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      id="warehouse-finance-return-outward-modal"
      className="fixed inset-0 z-50 mt-4 flex items-start justify-center bg-black/40 overflow-y-auto p-4 sm:p-8"
    >
      <div className="relative w-full sm:max-w-2xl max-h-[85vh] bg-white shadow-lg flex flex-col rounded-lg wh-modal-xs">
        <div className="border-b px-6 py-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-orange-600">
            <Icon path={mdiUndoVariant} size={1} color="orange" />
            Mark as Returned
          </h2>
          <p className="text-sm text-muted-foreground">
            You are about to mark{" "}
            <span className="font-semibold text-foreground">
              {selectedOutward.type === "SO"
                ? selectedOutward.order_id
                : selectedOutward.id}
            </span>{" "}
            as Returned. Allocated device movements
            {selectedOutward.type === "ST"
              ? " and in-transit entries"
              : ""}{" "}
            will be released back into stock.
          </p>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-6">
          <div className="space-y-2">
            <label className="text-base font-semibold">
              Return Reason <span className="text-red-600">*</span>
            </label>

            <textarea
              id="warehouse-finance-return-outward-reason-textarea"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              className="w-full rounded-md border px-3 py-2 text-sm"
              placeholder="Explain why this order/transfer is being returned..."
            />

            <p className="text-xs text-muted-foreground">
              Minimum 10 characters required.
            </p>

            {error && (
              <p className="text-sm text-red-600 font-medium">{error}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-base font-semibold">
              Attachment{" "}
              <span className="text-xs text-muted-foreground">(optional)</span>
            </label>

            <InwardAttachmentUpload
              accept="image/png,image/jpeg,image/jpg,application/pdf"
              maxSizeMB={5}
              files={files}
              multiple
              onChange={setFiles}
            />
          </div>
        </div>

        <div className="border-t px-6 py-4 flex justify-end gap-2">
          <button
            id="warehouse-finance-return-outward-cancel-btn"
            onClick={handleClose}
            disabled={loading}
            className="h-10 rounded-md border px-4 py-2 text-sm font-semibold"
          >
            Cancel
          </button>

          <button
            id="warehouse-finance-return-outward-submit-btn"
            onClick={handleReturnOutward}
            disabled={loading || isReasonInvalid}
            className={`h-10 rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white transition-all ${
              loading || isReasonInvalid
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-green-700"
            }`}
          >
            {loading ? "Processing..." : "Mark as Returned"}
          </button>
        </div>
      </div>

      {loading && <LoadingWidget />}
    </div>
  );
};

export default ReturnOutwardModal;
